/**
 * What an acknowledgement costs, said before the button rather than after.
 *
 * The same argument `RevokeConfirm` makes on `/devices`: this panel does not ask "are
 * you sure?". It reads out the five things an operator cannot find out afterwards,
 * each as its own list item with its own test id, and then offers the button.
 *
 * All five come from the endpoint's own description and from
 * `wp_alert_events_guard()`, which is the control that still holds when this file is
 * gone:
 *
 *  1. **It cannot be undone.** Every UPDATE to a row carrying `acknowledged_at` is
 *     refused at SQLSTATE 23001 — no un-acknowledge, no reassignment to another
 *     actor, and no correction of the note. So the note is written once or not at all.
 *  2. **Repeating it is a 200 no-op** returning the *original* actor and instant. The
 *     button is therefore safe to press twice, and a second press does not overwrite
 *     the first person's signature. That is what makes it retryable at all.
 *  3. **It starts the rule's cooldown**, from `acknowledged_at`. Firings inside the
 *     cooldown are recorded as suppressed occurrences — suppression is **not**
 *     deletion, each one is its own row with its own measurement.
 *  4. **A recurrence afterwards is a new alert**, with its own id and its own
 *     acknowledgement to come. Never a re-open of this one, because one person's
 *     signature must not silently cover a later event they did not see.
 *  5. **The note goes into the audit record**, verbatim, as the `reason` of the
 *     `wp_audit_log` row.
 *
 * The note is **optional** here and mandatory on an incident transition, which is not
 * an inconsistency: `AlertAcknowledgeRequest.note` is nullable on the wire, and a
 * client that demanded one would be inventing a rule the server does not have — the
 * mistake in the opposite direction from swallowing a refusal.
 */

import { useState, type ReactNode } from 'react';

import { useI18n } from '../../i18n';
import type { AlertSummary } from '../../api/types';
import type { WorkforceApiError } from '../../api/errors';
import { ApiErrorNote } from '../../states/ApiErrorNote';
import { StatusNote } from '../../states/StatusNote';

const NOTE_MAX = 1000;

export interface AcknowledgePanelProps {
  readonly alert: AlertSummary;
  readonly pending: boolean;
  readonly error: WorkforceApiError | null;
  readonly result: AlertSummary | null;
  readonly onSubmit: (note: string) => void;
  readonly onCancel: () => void;
}

export function AcknowledgePanel({
  alert,
  pending,
  error,
  result,
  onSubmit,
  onCancel,
}: AcknowledgePanelProps): ReactNode {
  const { t } = useI18n();
  const [note, setNote] = useState('');

  return (
    <section className="wp-panel" aria-labelledby="l5-ack-heading" data-testid="l5-ack-panel">
      <h3 className="wp-panel__title" id="l5-ack-heading">
        {t('alerts.ack.title', { id: alert.id })}
      </h3>

      <ul className="wp-list">
        <li data-testid="l5-ack-fact-irreversible">{t('alerts.ack.fact.irreversible')}</li>
        <li data-testid="l5-ack-fact-noop">{t('alerts.ack.fact.noop')}</li>
        <li data-testid="l5-ack-fact-cooldown">{t('alerts.ack.fact.cooldown')}</li>
        <li data-testid="l5-ack-fact-recurrence">{t('alerts.ack.fact.recurrence')}</li>
        <li data-testid="l5-ack-fact-audit">{t('alerts.ack.fact.audit')}</li>
      </ul>

      <div className="wp-field">
        <label className="wp-field__label" htmlFor="l5-ack-note">
          {t('alerts.ack.note')}
        </label>
        <input
          className="wp-field__input"
          id="l5-ack-note"
          data-testid="l5-ack-note"
          type="text"
          value={note}
          maxLength={NOTE_MAX}
          autoComplete="off"
          onChange={(event) => {
            setNote(event.target.value);
          }}
        />
      </div>

      <div className="wp-note__actions">
        <button
          className="wp-button wp-button--primary"
          type="button"
          data-testid="l5-ack-submit"
          disabled={pending}
          onClick={() => {
            onSubmit(note);
          }}
        >
          {pending ? t('alerts.ack.submitting') : t('alerts.ack.submit')}
        </button>
        <button
          className="wp-button wp-button--quiet"
          type="button"
          data-testid="l5-ack-cancel"
          onClick={onCancel}
        >
          {t('common.cancel')}
        </button>
      </div>

      {/* The server's own sentence, always. The reachable refusals here are a `409`
          on a suppressed occurrence — which names the alert it duplicates in
          `details.alert_id` — and the four chain refusals; both are rendered by
          `ApiErrorNote`, which branches on `code` and shows the message only where
          §5.1 marks it safe. Nothing is paraphrased and nothing is swallowed. */}
      {error === null ? null : (
        <ApiErrorNote error={error} subject={t('alerts.subjectWord')} testId="l5-ack-error" />
      )}

      {result === null ? null : (
        <StatusNote
          severity="low"
          title={t('alerts.ack.ok', {
            when: result.acknowledged_at ?? t('common.never'),
          })}
          testId="l5-ack-ok"
        >
          <p>{t('alerts.ack.okBody')}</p>
        </StatusNote>
      )}
    </section>
  );
}
